'use client';

import { GroupProps, useFrame, useThree } from '@react-three/fiber';
import {
  forwardRef,
  memo,
  MutableRefObject,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { Color, Group, Vector3 } from 'three';

import Atmosphere, { AtmosphereRef } from './Atmosphere/Atmosphere';
import Globe, { GlobeRef } from './Globe/Globe';

import useSphereRadius from 'hooks/useSphereRadius';

const ATMOSPHERE_DAY_COLOR = new Color('#8cb3d9'); // A lighter, more pastel blue
const ATMOSPHERE_TWILIGHT_COLOR = new Color('#9a94b3'); // A softer, less saturated purple-gray
export const SUN_DIRECTION = new Vector3(-0.8, 0, 0);

export interface EarthRef {
  sunDirection: MutableRefObject<Vector3>;
  playOutro: () => Promise<any>;
  playWatchAgain: () => Promise<any>;
  earth: MutableRefObject<Group | null>;
}

const Earth = forwardRef<EarthRef, GroupProps>(({ ...props }, ref) => {
  const predictedRadius = useSphereRadius(10, 0.9);
  const { camera } = useThree();

  const earthRef = useRef<Group>(null!);
  const globeRef = useRef<GlobeRef>(null);
  const atmosphereRef = useRef<AtmosphereRef>(null);
  const sunDirection = useRef<Vector3>(camera.position.clone().normalize());

  useFrame(() => {
    if (!globeRef.current?.sunDirection || !atmosphereRef.current?.sunDirection)
      return;

    globeRef.current.sunDirection.value = sunDirection.current;
    atmosphereRef.current.sunDirection.value = sunDirection.current;
  });

  useImperativeHandle(ref, () => ({
    earth: earthRef,
    sunDirection,
    playOutro: () => {
      return Promise.all([
        globeRef.current?.playOutro(),
        atmosphereRef.current?.playOutro(),
      ]);
    },
    playWatchAgain: () => {
      return Promise.all([
        globeRef.current?.playWatchAgain(),
        atmosphereRef.current?.playWatchAgain(),
      ]);
    },
  }));

  const radius = useMemo(() => Math.min(predictedRadius, 4), [predictedRadius]);

  const EarthGeometry = useMemo(
    () => <sphereGeometry args={[radius, 64, 64]} />,
    [radius]
  );

  const childProps = {
    dayColor: ATMOSPHERE_DAY_COLOR,
    twilightColor: ATMOSPHERE_TWILIGHT_COLOR,
    geometry: EarthGeometry,
    sunDirection: camera.position.clone().normalize(),
  };

  return (
    <group ref={earthRef} {...props} frustumCulled={false}>
      <Globe {...childProps} ref={globeRef} />
      <Atmosphere {...childProps} ref={atmosphereRef} />
    </group>
  );
});

Earth.displayName = 'Earth';
export default memo(Earth);
